/**
 * "Ask about this run" AI panel for Retail > Allocation.
 *
 * Renders below the KPI row and above the store x item grid on
 * /retail/allocation. Lets the operator ask a plain-English question about
 * the selected allocation run (a suggested chip, or free text) and shows
 * Claude's answer, which is generated from a compact digest of the run
 * (top stockouts, constrained items, worst stores, week-over-week deltas)
 * rather than the full multi-thousand-row line set.
 *
 * Asking a question does not hold a request open waiting for the answer.
 * It starts a job on the backend and this panel polls for the result (see
 * useAllocationInsights in useAllocation.ts), because the analysis can take
 * longer than a browser request is willing to stay open for. The hook also
 * resumes an in-progress or recently finished question on mount, so leaving
 * this page and coming back does not lose track of what was asked.
 *
 * Access is gated by the parent (Allocation.tsx only mounts this component
 * when the user has retail.allocation.read); this component just accepts a
 * runId and a disabled flag (true when no run is selected, or while the
 * page is otherwise busy) and does not re-check capabilities itself.
 */

import { useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, Loader2, Send } from 'lucide-react';

import { useAllocationInsights } from '@/lib/useAllocation';

const MAX_QUESTION_LENGTH = 600;

const SUGGESTED_QUESTIONS = [
  'Summarize this run',
  'What should we produce or buy more of?',
  'Which stores worry you?',
  'What changed vs last week?',
] as const;

type AllocationInsightsProps = {
  /** The run the question applies to. Null when no run is selected yet. */
  runId: number | null;
  /** True when asking should be unavailable (no run selected, page busy). */
  disabled?: boolean;
};

export function AllocationInsights({ runId, disabled = false }: AllocationInsightsProps) {
  const [expanded, setExpanded] = useState(true);
  const [question, setQuestion] = useState('');
  const insights = useAllocationInsights(runId);
  const isThinking = insights.phase === 'thinking';

  // A new run was selected: clear the composer so leftover text from the
  // prior run's question does not sit in the box. useAllocationInsights
  // clears and re-checks its own state for the new run on the same runId
  // change, so the answer area handles itself.
  useEffect(() => {
    setQuestion('');
  }, [runId]);

  const isDisabled = disabled || runId == null;
  const canSubmit = !isDisabled && !isThinking && question.trim().length > 0;

  function ask(text: string) {
    if (isDisabled || isThinking) return;
    const trimmed = text.trim();
    if (!trimmed) return;
    insights.ask(trimmed);
  }

  function handleChipClick(text: string) {
    setQuestion(text);
    ask(text);
  }

  function handleSubmit() {
    ask(question);
  }

  function handleInputKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
  }

  return (
    <div className="shrink-0 mb-4 rounded-lg border border-amp-line bg-amp-white dark:border-amp-dark-line dark:bg-amp-dark-surface">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        className="flex w-full items-center gap-1.5 px-4 py-3 text-left text-sm font-medium text-amp-ink dark:text-amp-dark-ink"
      >
        {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        Ask about this run
      </button>

      {expanded && (
        <div className="border-t border-amp-line px-4 py-3 dark:border-amp-dark-line">
          <div className="flex flex-wrap gap-2">
            {SUGGESTED_QUESTIONS.map((q) => (
              <button
                key={q}
                type="button"
                onClick={() => handleChipClick(q)}
                disabled={isDisabled || isThinking}
                className="rounded-full border border-amp-line px-3 py-1 text-xs text-amp-ink hover:bg-amp-cream-warm disabled:cursor-not-allowed disabled:opacity-60 dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm"
              >
                {q}
              </button>
            ))}
          </div>

          <div className="mt-3 flex items-center gap-2">
            <input
              type="text"
              value={question}
              onChange={(e) => setQuestion(e.target.value.slice(0, MAX_QUESTION_LENGTH))}
              onKeyDown={handleInputKeyDown}
              disabled={isDisabled || isThinking}
              maxLength={MAX_QUESTION_LENGTH}
              placeholder="Ask a question about this run..."
              aria-label="Ask about this run"
              className="flex-1 rounded border border-amp-line px-2 py-1.5 text-sm bg-amp-white text-amp-ink disabled:cursor-not-allowed disabled:opacity-60 dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
            />
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="inline-flex items-center gap-1 rounded border border-amp-ink bg-amp-ink px-3 py-1.5 text-xs text-amp-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60 dark:border-amp-dark-ink dark:bg-amp-dark-ink dark:text-amp-dark-surface"
            >
              {isThinking ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
              Ask
            </button>
          </div>

          {insights.question && insights.phase !== 'idle' && (
            <p className="mt-3 text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
              Asked: <span className="italic">&quot;{insights.question}&quot;</span>
            </p>
          )}

          {isThinking && (
            <p className="mt-3 flex items-center gap-2 text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
              Working on it
            </p>
          )}

          {insights.phase === 'error' && insights.errorMessage && (
            <p className="mt-3 text-sm text-red-700 dark:text-red-300">
              {insights.errorMessage}
            </p>
          )}

          {insights.phase === 'done' && insights.answer && (
            <div className="mt-3 rounded-lg border border-amp-line bg-amp-cream-warm p-3 text-sm text-amp-ink whitespace-pre-wrap dark:border-amp-dark-line dark:bg-amp-dark-surface-warm dark:text-amp-dark-ink">
              {insights.answer}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
